import RightPanelCard from "components/Common/RightPanelCard";
import CommonTable from "components/ReactTable/Table";
import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Container,
  Badge,
  FormGroup,
  Label,
  Input,
  CardFooter,
  UncontrolledTooltip,
} from "reactstrap";
import Select, { components } from "react-select";
import {
  customStyle,
  fixedWidthCustomStyle,
  DEFAULT_RECORDS_PER_PAGE,
  MASTER_STATUS_OPTIONS,
  MAX_FILE_SIZE,
  CREATED_BY,
} from "components/Common/constants";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import NotificationAlert from "react-notification-alert";
import { productList } from "constants/product";
import { getProductList, addProduct, exportProductData } from "actions/product";
import { productCategoryList } from "constants/productCategories";
import { manufacturerList } from "constants/manufacturer";
import { getManufacturerList } from "actions/manufacturer";
import { getProductCategoryList } from "actions/productCategories";
import { urlEncodedId } from "components/Common/encodeDecodeFunction";
import moment from "moment";

const initialFields = {
  name: {
    value: "",
    error: "",
  },
  category: {
    value: null,
    error: "",
  },
  manufacturer: {
    value: null,
    error: "",
  },
  modelNo: {
    value: "",
    error: "",
  },
  images: {
    value: [],
    error: "",
  },
  documents: {
    value: [{ title: "", file: "" }],
    error: "",
  },
  status: {
    value: "ACTIVE",
    error: "",
  },
};

function ProductListing() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const notificationAlertRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const productListData = useSelector((state) => state.products.productList);

  const productCategoryListData = useSelector(
    (state) => state.productCategory.productCategoryList,
  );
  const manufacturerListData = useSelector(
    (state) => state.manufacturer.manufacturerList,
  );

  const [productFormData, setProductFormData] = useState(initialFields);
  const [showLoaderOnClick, setShowLoaderOnClick] = useState(false);
  const [searchState, setSearchState] = useState({
    searchStrings: "",
    status: null,
    manufacturer: null,
    category: null,
    createdBy: null,
    filterParams: [],
    isSearched: false,
  });

  const [productListState, setProductListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnSearch: false,
  });
  const [pageNum, setPageNum] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);
  const formattedProductList = productListData?.data?.product.map(
    (item, index) => {
      return {
        ...item,
        srNo:
          (pageNum - 1) *
          (productListData?.data?.noOfRecordsPerPage ||
            DEFAULT_RECORDS_PER_PAGE) +
          index +
          1,
      };
    },
  );

  useEffect(() => {
    const filterJSON = [
      {
        field: "Status",
        condition: "eq",
        filterValues: ["ACTIVE"],
      },
    ];
    getManufacturerList(dispatch, { filters: JSON.stringify(filterJSON) });
    getProductCategoryList(dispatch);

    return () => {
      dispatch({ type: manufacturerList, payload: {} });
      dispatch({ type: productCategoryList, payload: {} });
    };
  }, [dispatch]);

  //to make the scroll on the parent component closed when child component is active
  useEffect(() => {
    if (isModalOpen) {
      // Disable scrolling on body
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    // Clean up on unmount
    return () => {
      document.body.style.overflow = "";
    };
  }, [isModalOpen]);

  useEffect(() => {
    getProductList(dispatch, {
      pageNum: pageNum,
      filters: searchState.filterParams?.filters || "",
      searchString: searchState.filterParams?.searchString || "",
    });

    return () => {
      dispatch({ type: productList, payload: {} });
    };
  }, [dispatch, pageNum, searchState.filterParams]);

  //to check the code and message
  useEffect(() => {
    if (productListData?.code === 200) {
      setProductListState({
        isLoading: false,
        error: "",
        noRecords: productListData?.data?.product?.length === 0,
        showLoaderOnSearch: false,
      });
    } else if (productListData?.code || productListData?.message) {
      setProductListState({
        isLoading: false,
        error:
          productListData?.message ||
          "Failed to load product category list! Please try again.",
        noRecords: false,
        showLoaderOnSearch: false,
      });
    }
  }, [productListData]);

  const openAddModal = () => {
    setIsModalOpen(true);
  };

  const closeAddModal = () => {
    getProductCategoryList(dispatch);
    setIsModalOpen(false);
    setProductFormData({
      ...initialFields,
      images: { value: [], error: "" },
      documents: { value: [{ title: "", file: "" }], error: "" },
    });
  };

  const fetchCategoryByManufacturer = (manufacturerID) => {
    if (!manufacturerID) {
      getProductCategoryList(dispatch);
      return;
    }

    const filterJSON = JSON.stringify([
      {
        field: "ManufacturerID",
        condition: "eq",
        filterValues: [String(manufacturerID)],
      },
    ]);

    getProductCategoryList(dispatch, { filters: filterJSON });
  };

  const handleChange = (fieldName, newValue) => {
    if (fieldName === "manufacturer") {
      setProductFormData((prev) => ({
        ...prev,
        manufacturer: {
          ...prev.manufacturer,
          value: newValue,
          error: "",
        },
        category: {
          value: null,
          error: "",
        },
      }));

      if (newValue?.ID) {
        fetchCategoryByManufacturer(newValue.ID);
      }

      return;
    }

    setProductFormData((prev) => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        value: newValue,
        isChanged: false,
        error: "",
      },
    }));
  };

  const handleOnRowClick = (rowData) => {
  const encodedId = urlEncodedId(rowData.ID);

  window.open(
    `/admin/products/${encodedId}/details`,
    "_blank",
    "noopener,noreferrer",
  );
};
  // Custom Option Renderer
  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  // Custom SingleValue Renderer (for the selected value)
  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };
  const buildFilterParams = () => {
    const filterParams = [];

    if (searchState.category?.ID) {
      filterParams.push({
        field: "ProductCategoryID",
        condition: "eq",
        filterValues: [String(searchState.category.ID)],
      });
    }

    if (searchState.manufacturer?.ID) {
      filterParams.push({
        field: "ManufacturerID",
        condition: "eq",
        filterValues: [String(searchState.manufacturer.ID)],
      });
    }

    if (searchState.createdBy?.value) {
      filterParams.push({
        field: "CreatedBy",
        condition: "eq",
        filterValues: [searchState.createdBy.value],
      });
    }

    if (searchState.status?.value) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [searchState.status.value],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : "";
  };

  const handleSearch = () => {
    const searchString = searchState.searchStrings.trim();

    const newParams = {
      filters: buildFilterParams(),
      searchString,
    };

    // Nothing changed AND we're not clearing an active search -> no-op
    if (
      JSON.stringify(searchState.filterParams) === JSON.stringify(newParams)
    ) {
      return;
    }

    setProductListState((prev) => ({
      ...prev,
      showLoaderOnSearch: true,
      isLoading: true,
    }));

    setSearchState((prev) => ({
      ...prev,
      filterParams: newParams,
      isSearched: Boolean(
        searchString ||
          searchState.category?.ID ||
          searchState.manufacturer?.ID ||
          searchState.status?.value ||
          searchState.createdBy?.value,
      ),
    }));

    if (pageNum !== 1) setPageNum(1);
  };

  const clearSearch = () => {
    setProductListState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    setSearchState({
      searchStrings: "",
      status: null,
      manufacturer: null,
      category: null,
      createdBy: null,
      filterParams: [],
      isSearched: false,
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const notify = (type, message) => {
    const options = {
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };
    notificationAlertRef.current.notificationAlert(options);
  };

  const validateForm = () => {
    let product = { ...productFormData };
    let isValid = true;
    Object.keys(product).forEach((key) => {
      if (key === "documents") {
        const docs = product[key].value || [];

        docs.forEach((doc) => {
          const errors = getValidationError("document", doc);

          if (errors.titleError || errors.fileError) {
            isValid = false;
          }

          doc.titleError = errors.titleError;
          doc.fileError = errors.fileError;
        });

        product[key] = {
          ...product[key],
          value: docs,
        };
      } else {
        const error = getValidationError(key, product[key].value);

        product[key] = {
          ...product[key],
          error,
        };

        if (error) {
          isValid = false;
        }
      }
    });

    setProductFormData(product);
    return isValid;
  };

  const handleSaveClick = async () => {
    if (!validateForm()) return;
    setShowLoaderOnClick(true);
    getProductCategoryList(dispatch);
    try {
      const formData = new FormData();
      const productData = {
        manufacturerID: productFormData.manufacturer.value?.ID,
        productCategoryID: productFormData.category.value?.ID,
        name: productFormData.name.value,
        modelNo: productFormData.modelNo.value,
        description: productFormData.description?.value || "",
        status: productFormData.status.value,
      };
      formData.append("product", JSON.stringify(productData));

      if (productFormData.images?.value?.length > 0) {
        formData.append("product image", productFormData.images.value[0].file);
      }

      const docs = productFormData.documents.value;
      const limitedDocs = docs.slice(0, 5);

      limitedDocs.forEach((doc, index) => {
        if (doc.file && doc.title) {
          formData.append(doc.title, doc.file);
        }
      });

      const res = await addProduct(formData);

      if (res.code === 200) {
        notify("success", res?.message || "Product added successfully");
        closeAddModal();

        pageNum === 1
          ? getProductList(dispatch, { pageNum: pageNum })
          : setPageNum(1);
      } else {
        notify("danger", res?.message || "Failed to add product.");
      }
    } catch (error) {
      notify("danger", error?.message || "Failed to add product.");
    } finally {
      setShowLoaderOnClick(false);
    }
  };

  const getValidationError = (name, value) => {
    switch (name) {
      case "name":
        return !value ? "Please enter a product name" : "";
      case "manufacturer":
        return !value ? "Please select a manufacturer" : "";
      case "category":
        return !value ? "Please select a category" : "";
      case "modelNo":
        return !value ? "Please enter a model number" : "";
      case "document":
        return {
          titleError:
            (value?.file || value?.url) && !value?.title?.trim()
              ? "Please enter document title"
              : "",

          fileError:
            value?.title?.trim() && !value?.file && !value?.url
              ? "Please upload PDF"
              : "",
        };
      default:
        return "";
    }
  };

  const validateField = (name, value, index = null) => {
    const error = getValidationError(name, value);

    if (name === "document") {
      setProductFormData((prev) => {
        const updatedDocs = [...prev.documents.value];

        updatedDocs[index] = {
          ...updatedDocs[index],
          titleError: error.titleError,
          fileError: error.fileError,
        };

        return {
          ...prev,
          documents: {
            ...prev.documents,
            value: updatedDocs,
          },
        };
      });
      return;
    }

    setProductFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        error,
      },
    }));
  };

  const handleOnBlur = (name, value) => {
    validateField(name, value);
  };

  const handleDocumentBlur = (index) => {
    validateField("document", productFormData.documents.value[index], index);
  };

  const handleImageChange = (file) => {
    if (!validateFileSize(file, "Image")) return;

    const newImage = { file, preview: URL.createObjectURL(file) };
    handleChange("images", [newImage]); // store as array
  };

  const validateFileSize = (file, type = "File") => {
    if (!file) return false;
    if (file.size > MAX_FILE_SIZE) {
      notify("danger", `${type} size should not exceed 10 MB`);
      return false;
    }
    return true;
  };
  
  const columns = [
  {
    Header: "Sr. No.",
    accessor: "srNo",
    centerAlign: true,
    className: "col-srNo",
  },
  {
    Header: "Name",
    accessor: "name",
    className: "col-product",
    // ❌ REMOVED the stopPropagation wrapper
    Cell: ({ value }) => value || "-",
  },
  {
    Header: "Manufacturer",
    accessor: "manufacturer",
    className: "col-manufacturer",
    // ❌ REMOVED the stopPropagation wrapper
    Cell: ({ value }) => value?.name || "-",
  },
  {
    Header: "Category",
    accessor: "productCategory",
    className: "col-pl-category",
    Cell: ({ value }) => value?.category || "",
  },
  {
    Header: "Model Number",
    accessor: "modelNo",
    className: "col-pl-model",
    // ❌ REMOVED the stopPropagation wrapper
    Cell: ({ value }) => value || "-",
  },
  {
    Header: "Created By",
    accessor: "createdBy",
    className: "col-createdBy",
    Cell: ({ value }) =>
      CREATED_BY.find((item) => item.value === value)?.label || "-",
  },
  {
    Header: "Status",
    accessor: "status",
    className: "col-status",
    Cell: ({ value }) => (
      <Badge color={value === "ACTIVE" ? "success" : "warning"} pill>
        {value}
      </Badge>
    ),
  },
  {
    Header: "Actions",
    accessor: "ID",
    className: "col-actions",
    disableSortBy: true,
    // ✅ KEEP stopPropagation HERE so clicking Edit doesn't trigger row navigation
    Cell: ({ value }) => {
      const encodedId = urlEncodedId(value);
      return (
        <div
          className="actions-right d-flex justify-content-start"
          onClick={(e) => e.stopPropagation()} // <-- KEEP this
        >
          <Button
            className="btn-link btn-icon icon-info"
            id={`edit${value}`}
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              window.open(
                `/admin/products/${encodedId}/details?edit=true`,
                "_blank",
                "noopener,noreferrer",
              );
            }}
          >
            <i className="tim-icons icon-pencil tim-md" />
          </Button>
          <UncontrolledTooltip delay={0} target={`edit${value}`}>
            Edit
          </UncontrolledTooltip>
        </div>
      );
    },
  },
];

  const downloadCSVFiles = async () => {
    try {
      setIsDownloading(true);
      const res = await exportProductData({
        filters: searchState.filterParams?.filters || "",
        searchString: searchState.filterParams?.searchString || "",
      }
      );
      if (res.status === 200) {
        const blob = new Blob(["\uFEFF" + res?.data], {
          type: "text/csv;charset=utf-8;",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = `products-report_${moment().format("YYYY-MM-DD")}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        notify("success", res?.message || "CSV exported successfully");
      } else {
        const message = Array.isArray(res?.message || res?.data?.message)
          ? res?.message[0]?.Msg || res?.data?.message[0]?.Msg
          : res?.message || res?.data?.message || "Failed to export csv, try again later";
        notify("danger", message);
      }
    } catch (error) {
      notify("danger", "Failed to download product file succesfully.");
    } finally {
      setIsDownloading(false);
    }
  };
  return (
    <>
      {isDownloading && (
        <div className="loading-overlay">
          <div className="loading-card">
            <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-3" />
            <div className="font-weight-bold text-dark">
              Processing… Please wait.
            </div>
          </div>
        </div>
      )}
      {/* <div className="content"> */}
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <Container fluid className="mt-4">
        <Row>
          <Col className="mb-5" md="12">
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between mb-2">
                <CardTitle tag="h3" className="font-weight-normal">
                  Products
                </CardTitle>
                <div className="d-flex align-items-center">
                  <Button
                    className="btn-simple btn-round btn-style mr-3"
                    color="default"
                    size="md"
                    onClick={downloadCSVFiles}
                  >
                    <i className="fas fa-arrow-up-from-bracket mr-1" /> Export
                    Products
                  </Button>
                  <Button
                    className="btn-round btn-style"
                    color="default"
                    size="md"
                    onClick={openAddModal}
                  >
                    <i className="fa fa-plus mr-1" />
                    Add Product
                  </Button>
                </div>
              </CardHeader>
              {/* <span className="text-danger">
                export Product *API integrated but according to filters data get
                downloaded
              </span> */}
              <CardBody className="pt-2">
                <div className="d-flex align-items-center justify-content-between flex-wrap flex-column flex-md-row">
                  {/* Left side - Filter Inputs + Search */}
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder="Search by name or model no."
                      className="mr-2 mb-2 filter-input"
                      value={searchState.searchStrings}
                      onChange={(e) =>
                        setSearchState((prev) => ({
                          ...prev,
                          searchStrings: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                      maxLength={150}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={manufacturerListData?.data?.manufacturer || []}
                      placeholder="Manufacturer"
                      classNamePrefix="react-select"
                      value={searchState.manufacturer}
                      getOptionLabel={(options) => options.name}
                      getOptionValue={(options) => options.ID}
                      styles={fixedWidthCustomStyle()}
                      onChange={(opt) => {
                        setSearchState((prev) => ({
                          ...prev,
                          manufacturer: opt,
                          category: null,
                        }));

                        fetchCategoryByManufacturer(opt?.ID);
                      }}
                      onKeyDown={handleKeyDown}
                    />
                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={
                        productCategoryListData?.data?.productCategory || []
                      }
                      placeholder="Category"
                      classNamePrefix="react-select"
                      value={searchState.category}
                      isDisabled={!searchState.manufacturer}
                      getOptionLabel={(options) => options.category}
                      getOptionValue={(options) => options.ID}
                      styles={fixedWidthCustomStyle()}
                      onChange={(opt) =>
                        setSearchState((prev) => ({
                          ...prev,
                          category: opt,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={CREATED_BY}
                      placeholder="Created by"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      value={searchState.createdBy}
                      onChange={(opt) =>
                        setSearchState((prev) => ({
                          ...prev,
                          createdBy: opt,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={MASTER_STATUS_OPTIONS}
                      placeholder="Status"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      components={{ Option, SingleValue }}
                      value={searchState.status}
                      onChange={(opt) =>
                        setSearchState((prev) => ({
                          ...prev,
                          status: opt,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Button
                      color="default"
                      className="d-flex justify-content-center align-items-center mb-3 mt-2 filter-btn btn-simple"
                      onClick={handleSearch}
                    >
                      {productListState.showLoaderOnSearch ? (
                        <>
                          <i className="fa fa-spinner fa-spin search-loader" />{" "}
                        </>
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" /> Search
                        </>
                      )}
                    </Button>
                    {(searchState.filterParams.length > 0 ||
                      searchState.isSearched) && (
                        <Button
                          color="default"
                          className="d-flex justify-content-center align-items-center mb-3 mt-2 ml-2 filter-btn btn-simple"
                          onClick={clearSearch}
                        >
                          <i className="fa fa-times mr-1" /> Clear
                        </Button>
                      )}
                  </div>
                </div>
                <div className="mt-2 mb-3 mx-1">
                  {productListData?.data?.totalRecords ? (
                    <span className="font-weight-bold">
                      {searchState.isSearched ||
                        searchState.filterParams?.length > 0
                        ? `Showing ${productListData?.data?.totalFilteredRecords} of ${productListData?.data?.totalRecords} Products`
                        : `Total Products: ${productListData?.data?.totalRecords}`}
                    </span>
                  ) : null}
                </div>

                {productListState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : productListState.error ? (
                  <>
                    <div className="loaderContainer">
                      {productListState.error}
                    </div>
                  </>
                ) : productListState.noRecords ? (
                  <div className="loaderContainer text-muted">
                    No product added yet
                  </div>
                ) : (
                  <div className="table-responsive product-table">
                    <CommonTable
                      data={formattedProductList || []}
                      resizable={false}
                      columns={columns}
                      defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                      showPaginationBottom
                      pageNum={pageNum}
                      setPageNum={setPageNum}
                      className="-striped -highlight table-hover"
                      rowClick={handleOnRowClick}
                      totalPages={Math.ceil(
                        productListData?.data?.totalFilteredRecords /
                        (productListData?.data?.noOfRecordsPerPage ||
                          DEFAULT_RECORDS_PER_PAGE),
                      )}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
        <RightPanelCard isOpen={isModalOpen} onClose={closeAddModal}>
          <Card className="location-card  mb-0">
            <CardHeader
              className="d-flex align-items-center justify-content-between mb-2 flex-shrink-0"
              tag="h4"
            >
              Add Product
              <i
                className="fa fa-close text-muted pointer"
                onClick={closeAddModal}
              />
            </CardHeader>

            <CardBody className="scrollbar-inner overflow-auto">
              <Row>
                <Col md="6" className="mb-3">
                  <FormGroup
                    className={`has-label ${productFormData.name.error ? "has-danger" : ""
                      } d-flex flex-column align-items-start`}
                  >
                    <Label>
                      Product Name <span className="validation-error">*</span>
                    </Label>
                    <Input
                      name="name"
                      type="text"
                      placeholder="Enter product name"
                      autoComplete="off"
                      value={productFormData.name.value}
                      onChange={(e) =>
                        handleChange(e.target.name, e.target.value)
                      }
                      onBlur={(e) =>
                        handleOnBlur(e.target.name, e.target.value)
                      }
                      // as per backend validation maxLength 100
                      maxLength={100}
                      required
                    />
                    {productFormData.name.error && (
                      <small className="validation-error">
                        {productFormData.name.error}
                      </small>
                    )}
                  </FormGroup>
                </Col>
                <Col md="6" className="mb-3">
                  <FormGroup
                    className={`has-label ${productFormData.manufacturer.error ? "has-danger" : ""
                      } d-flex flex-column align-items-start`}
                  >
                    <Label>
                      Manufacturer <span className="validation-error">*</span>
                    </Label>
                    <Select
                      className={`text-left ${productFormData.manufacturer.error ? "is-invalid" : ""
                        }`}
                      options={manufacturerListData?.data?.manufacturer}
                      placeholder="Select manufacturer"
                      classNamePrefix="react-select"
                      styles={customStyle}
                      value={productFormData.manufacturer.value || null}
                      getOptionLabel={(options) => options.name}
                      getOptionValue={(options) => options.ID}
                      onChange={(selected) =>
                        handleChange("manufacturer", selected)
                      }
                      onBlur={() =>
                        handleOnBlur(
                          "manufacturer",
                          productFormData.manufacturer.value,
                        )
                      }
                      menuPosition="fixed"
                      menuPortalTarget={document.body}
                    />
                    {productFormData.manufacturer.error && (
                      <small className="validation-error">
                        {productFormData.manufacturer.error}
                      </small>
                    )}
                  </FormGroup>
                </Col>
                <Col md="6" className="mb-3">
                  <FormGroup
                    className={`has-label ${productFormData.category.error ? "has-danger" : ""
                      } d-flex flex-column align-items-start`}
                  >
                    <Label>
                      Category <span className="validation-error">*</span>
                    </Label>
                    <Select
                      className={`text-left ${productFormData.category.error ? "is-invalid" : ""
                        }`}
                      options={productCategoryListData?.data?.productCategory}
                      placeholder="Select category"
                      classNamePrefix="react-select"
                      styles={customStyle}
                      value={productFormData.category.value || null}
                      isDisabled={!productFormData.manufacturer.value}
                      getOptionLabel={(options) => options.category}
                      getOptionValue={(options) => options.ID}
                      onChange={(selected) =>
                        handleChange("category", selected)
                      }
                      onBlur={() =>
                        handleOnBlur("category", productFormData.category.value)
                      }
                      menuPosition="fixed"
                      menuPortalTarget={document.body}
                    />
                    {productFormData.category.error && (
                      <small className="validation-error">
                        {productFormData.category.error}
                      </small>
                    )}
                  </FormGroup>
                </Col>
                <Col md="6" className="mb-3">
                  <FormGroup
                    className={`has-label ${productFormData.modelNo.error ? "has-danger" : ""
                      } d-flex flex-column align-items-start`}
                  >
                    <Label for="modelNo">
                      Model Number <span className="validation-error">*</span>
                    </Label>
                    <Input
                      name="modelNo"
                      type="text"
                      placeholder="Enter model number"
                      autoComplete="off"
                      value={productFormData.modelNo.value}
                      onChange={(e) =>
                        handleChange(e.target.name, e.target.value)
                      }
                      onBlur={(e) =>
                        handleOnBlur(e.target.name, e.target.value)
                      }
                      //as per backend validation
                      maxLength={100}
                      required
                    />
                    {productFormData.modelNo.error && (
                      <small className="validation-error">
                        {productFormData.modelNo.error}
                      </small>
                    )}
                  </FormGroup>
                </Col>

                <Col md="12" className="mb-3">
                  <FormGroup className="d-flex flex-column align-items-start w-100">
                    <Label>
                      Product Image{" "}
                      <span className="text-muted mb-1">
                        (Accepted image formats: PNG, JPEG, JPG. Maximum file
                        size: 10 MB)
                      </span>
                    </Label>
                    <div
                      className="product-image-upload w-100 p-3 border rounded text-center"
                      onDragOver={(e) => e.preventDefault()}
                      onDragEnter={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const file = e.dataTransfer.files[0];
                        if (file && file.type.startsWith("image/")) {
                          handleImageChange(file);
                        }
                      }}
                    >
                      {productFormData.images?.value?.length > 0 ? (
                        <div className="d-flex flex-wrap justify-content-start">
                          {productFormData.images.value.map((img, index) => (
                            <div
                              className="image-preview-wrapper mr-2 mb-2 text-center"
                              key={index}
                            >
                              <img
                                src={img.preview}
                                alt={`Product ${index}`}
                                className="img-fluid rounded product-preview-img"
                              />
                              <div className="small text-muted mt-1">
                                {img.file?.name}
                              </div>

                              <span
                                className="remove-image"
                                onClick={() => {
                                  const newImages =
                                    productFormData.images.value.filter(
                                      (_, i) => i !== index,
                                    );
                                  handleChange("images", newImages);
                                }}
                              >
                                &times;
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <>
                          <i className="fa fa-cloud-upload fa-2x text-muted mb-2"></i>
                          <p className="mb-1 text-muted">
                            Click or drag image to upload
                          </p>
                          <Label
                            for="productImageInput"
                            className="btn btn-simple btn-sm mt-2"
                          >
                            Choose Image
                          </Label>
                        </>
                      )}

                      <Input
                        type="file"
                        name="image"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) handleImageChange(file);
                          e.target.value = "";
                        }}
                        className="d-none"
                        id="productImageInput"
                      />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="12" className="mb-4">
                  <FormGroup className="w-100">
                    <Label className="fw-bold mb-3">
                      Add PDF Document{" "}
                      <span className="text-muted mb-1">
                        (Maximum size allowed per pdf : 10 MB)
                      </span>
                    </Label>

                    <div className="document-section w-100 p-3 rounded border">
                      {(productFormData.documents?.value || []).map(
                        (doc, index) => {
                          const totalDocs =
                            productFormData.documents.value.length;
                          return (
                            <Row key={index} className="mb-2 p-0">
                              {/* Document Title */}
                              <Col xs="12" md="5">
                                <FormGroup>
                                  <Label className="form-label text-muted small mb-1">
                                    Document Title
                                  </Label>
                                  <Input
                                    type="text"
                                    placeholder="Enter document title"
                                    value={doc.title || ""}
                                    onChange={(e) => {
                                      const updatedDocs = [
                                        ...productFormData.documents.value,
                                      ];
                                      updatedDocs[index].title = e.target.value;
                                      handleChange("documents", updatedDocs);
                                    }}
                                    maxLength={100}
                                    onBlur={() => handleDocumentBlur(index)}
                                  />
                                  {doc.titleError && (
                                    <small className="validation-error">
                                      {doc.titleError}
                                    </small>
                                  )}
                                </FormGroup>
                              </Col>

                              {/* Upload PDF */}
                              <Col xs="12" md="5">
                                <FormGroup>
                                  <Label className="form-label text-muted small mb-1">
                                    Upload PDF
                                  </Label>

                                  <div className="custom-file-wrapper">
                                    <Input
                                      type="file"
                                      accept="application/pdf"
                                      className="custom-file-input"
                                      id={`documentFile-${index}`}
                                      onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file.size > MAX_FILE_SIZE) {
                                          notify(
                                            "danger",
                                            "Document size should not exceed 10 MB",
                                          );
                                          e.target.value = ""; // reset input
                                          return;
                                        }
                                        const updatedDocs = [
                                          ...productFormData.documents.value,
                                        ];

                                        if (file) {
                                          updatedDocs[index].file = file;
                                          updatedDocs[index].name = file.name;
                                          handleChange(
                                            "documents",
                                            updatedDocs,
                                          );
                                        }

                                        e.target.value = ""; // allow reselecting same file
                                      }}
                                      onBlur={() => handleDocumentBlur(index)}
                                    />
                                    {doc.fileError && (
                                      <small className="validation-error">
                                        {doc.fileError}
                                      </small>
                                    )}
                                    <label
                                      htmlFor={`documentFile-${index}`}
                                      className={`custom-file-label ${doc?.name ? "has-file" : ""
                                        }`}
                                    >
                                      <span className="file-name">
                                        {doc?.name || "Choose PDF"}
                                      </span>
                                    </label>
                                  </div>
                                </FormGroup>
                              </Col>

                              {/* Action Buttons */}
                              <Col
                                xs="12"
                                md="2"
                                className="d-flex align-items-center mt-md-1 mt-0"
                              >
                                {/* Remove button (show if not first or if more than one doc) */}
                                {totalDocs > 1 && (
                                  <Button
                                    color="default"
                                    size="sm"
                                    className="btn-icon btn-simple  mr-2"
                                    onClick={() => {
                                      const newDocs =
                                        productFormData.documents.value.filter(
                                          (_, i) => i !== index,
                                        );
                                      handleChange("documents", newDocs);
                                    }}
                                  >
                                    <i className="fa fa-times" />
                                  </Button>
                                )}
                                {/* Add button (only on last record AND only if less than 5 docs) */}
                                {index === totalDocs - 1 && totalDocs < 5 && (
                                  <Button
                                    color="default"
                                    size="sm"
                                    className="btn-icon"
                                    onClick={() => {
                                      const newDocs = [
                                        ...(productFormData.documents?.value ||
                                          []),
                                        { title: "", file: null, name: "" },
                                      ];
                                      handleChange("documents", newDocs);
                                    }}
                                  >
                                    <i className="fa fa-plus" />
                                    {/* Add */}
                                  </Button>
                                )}
                              </Col>
                            </Row>
                          );
                        },
                      )}
                    </div>
                  </FormGroup>
                </Col>
                <Col md="6" className="mb-3">
                  <FormGroup className="status-group-product">
                    {/* Label for the whole group */}
                    <Label for="status" className="mb-0">
                      Status <span className="text-danger ml-1">*</span>
                    </Label>

                    {/* Active */}
                    <div className="form-check-product">
                      <Input
                        type="radio"
                        name="status"
                        id="statusActive"
                        value="ACTIVE"
                        checked={productFormData.status.value === "ACTIVE"}
                        onChange={(e) =>
                          handleChange(e.target.name, e.target.value)
                        }
                      />
                      <Label for="statusActive" className="mb-0">
                        {/* <Badge color="success" pill> */}
                        Active
                        {/* </Badge> */}
                      </Label>
                    </div>

                    {/* Inactive */}
                    <div className="form-check-product">
                      <Input
                        type="radio"
                        name="status"
                        id="statusInactive"
                        value="INACTIVE"
                        checked={productFormData.status.value === "INACTIVE"}
                        onChange={(e) =>
                          handleChange(e.target.name, e.target.value)
                        }
                      />
                      <Label for="statusInactive" className="mb-0">
                        {/* <Badge color="warning" pill> */}
                        Inactive
                        {/* </Badge> */}
                      </Label>
                    </div>
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
            <CardFooter>
              <div className="d-flex justify-content-end mb-0">
                <Button
                  color="secondary"
                  onClick={closeAddModal}
                  className="mr-2"
                >
                  Cancel
                </Button>
                <Button
                  color="default"
                  type="submit"
                  onClick={() => {
                    handleSaveClick();
                  }}
                  disabled={
                    Object.values(productFormData).some(
                      (item) => item.error !== "",
                    ) ||
                    productFormData.documents.value.some(
                      (doc) => doc.titleError || doc.fileError,
                    )
                  }
                >
                  {showLoaderOnClick ? (
                    <i className="fa fa-spinner fa-spin fa-lg text-white" />
                  ) : (
                    "Save"
                  )}
                </Button>
              </div>
            </CardFooter>
          </Card>
        </RightPanelCard>
      </Container>
    </>
  );
}

export default ProductListing;
